/**
 * Google Calendar 模組 - 服務層
 * 處理訂單與 Google Calendar 的同步操作
 */

import { supabase } from '../../services/supabaseClient';
import type {
  CalendarEventData,
  SyncCalendarParams,
  CalendarResult,
  CalendarEventExistsResult,
  CalendarEdgeFunctionParams,
} from './types';
import {
  buildCalendarEventData,
  parseCalendarError,
} from './helpers';

/**
 * 檢查訂單是否已同步至日曆
 */
export async function checkCalendarEventExists(
  orderId: string
): Promise<CalendarEventExistsResult> {
  const { data: existingEvent } = await supabase
    .from('order_calendar_events')
    .select('id, google_event_id, event_title, created_at')
    .eq('order_id', orderId)
    .maybeSingle();

  return {
    exists: !!existingEvent,
    eventData: existingEvent || undefined,
  };
}

/**
 * 呼叫 Edge Function 執行日曆操作
 */
async function invokeCalendarFunction(
  params: CalendarEdgeFunctionParams
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
    body: params,
  });

  if (error) {
    throw error;
  }

  return data;
}

/**
 * 建立日曆事件資料
 */
function createEventData(params: SyncCalendarParams): CalendarEventData {
  return buildCalendarEventData(params);
}

/**
 * 同步訂單到 Google Calendar
 * 在訂單簽約完成時呼叫
 */
export async function syncOrderToCalendar(
  params: SyncCalendarParams
): Promise<CalendarResult> {
  try {
    // 檢查是否已經同步過
    const { exists } = await checkCalendarEventExists(params.orderId);

    if (exists) {
      return {
        success: false,
        message: '此訂單已同步至日曆，如需更新請使用重新同步功能',
      };
    }

    // 產生事件資料
    const eventData = createEventData(params);

    // 呼叫 Edge Function 建立日曆事件
    const result = await invokeCalendarFunction({
      action: 'create_event',
      order_id: params.orderId,
      location_id: params.pickupLocationId,
      event_data: eventData,
    });

    if (!result.success) {
      throw new Error(result.error || '同步失敗');
    }

    return {
      success: true,
      message: `✅ 已同步至 ${params.pickupLocationName} 日曆`,
    };
  } catch (error: any) {
    console.error('同步日曆失敗:', error);
    return {
      success: false,
      message: parseCalendarError(error),
    };
  }
}

/**
 * 刪除舊的日曆事件記錄
 */
async function deleteOldCalendarEvent(
  orderId: string,
  locationId: string
): Promise<void> {
  try {
    await invokeCalendarFunction({
      action: 'delete_event',
      order_id: orderId,
      location_id: locationId,
    });
  } catch (e) {
    console.warn('刪除舊日曆事件時發生錯誤，將直接建立新事件:', e);
  }

  // 無論刪除是否成功，都先清除資料庫記錄
  await supabase
    .from('order_calendar_events')
    .delete()
    .eq('order_id', orderId);
}

/**
 * 重新同步訂單到 Google Calendar
 * 先刪除舊事件，再建立新事件
 * 用於訂單資料修改後需要更新日曆的情況
 */
export async function resyncOrderToCalendar(
  params: SyncCalendarParams
): Promise<CalendarResult> {
  try {
    // 檢查是否有舊的日曆事件
    const { exists } = await checkCalendarEventExists(params.orderId);

    // 如果有舊事件，先刪除
    if (exists) {
      await deleteOldCalendarEvent(params.orderId, params.pickupLocationId);
    }

    // 建立新的日曆事件
    const eventData = createEventData(params);

    const result = await invokeCalendarFunction({
      action: 'create_event',
      order_id: params.orderId,
      location_id: params.pickupLocationId,
      event_data: eventData,
    });

    if (!result.success) {
      throw new Error(result.error || '同步失敗');
    }

    return {
      success: true,
      message: exists
        ? `✅ 已更新 ${params.pickupLocationName} 日曆（舊事件已刪除）`
        : `✅ 已同步至 ${params.pickupLocationName} 日曆`,
    };
  } catch (error: any) {
    console.error('重新同步日曆失敗:', error);
    return {
      success: false,
      message: parseCalendarError(error),
    };
  }
}

/**
 * 更新日曆事件
 * 當訂單資訊修改時呼叫
 */
export async function updateCalendarEvent(
  params: SyncCalendarParams
): Promise<CalendarResult> {
  try {
    // 檢查是否有同步記錄
    const { exists } = await checkCalendarEventExists(params.orderId);

    if (!exists) {
      return {
        success: false,
        message: '此訂單尚未同步至日曆',
      };
    }

    // 產生更新的事件資料
    const eventData = createEventData(params);

    // 呼叫 Edge Function 更新日曆事件
    const result = await invokeCalendarFunction({
      action: 'update_event',
      order_id: params.orderId,
      location_id: params.pickupLocationId,
      event_data: eventData,
    });

    if (!result.success) {
      throw new Error(result.error || '更新失敗');
    }

    return {
      success: true,
      message: '✅ 日曆事件已更新',
    };
  } catch (error: any) {
    console.error('更新日曆事件失敗:', error);
    return {
      success: false,
      message: parseCalendarError(error),
    };
  }
}

/**
 * 刪除日曆事件
 * 當訂單取消時呼叫
 */
export async function deleteCalendarEvent(
  orderId: string,
  pickupLocationId: string
): Promise<CalendarResult> {
  try {
    // 檢查是否有同步記錄
    const { exists } = await checkCalendarEventExists(orderId);

    if (!exists) {
      return {
        success: false,
        message: '此訂單尚未同步至日曆',
      };
    }

    // 呼叫 Edge Function 刪除日曆事件
    const result = await invokeCalendarFunction({
      action: 'delete_event',
      order_id: orderId,
      location_id: pickupLocationId,
    });

    if (!result.success) {
      throw new Error(result.error || '刪除失敗');
    }

    return {
      success: true,
      message: '✅ 日曆事件已刪除',
    };
  } catch (error: any) {
    console.error('刪除日曆事件失敗:', error);
    return {
      success: false,
      message: parseCalendarError(error),
    };
  }
}
